var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { AppConfig } from '../../config/config';
import { getBoostSessionId, getCustomerId } from './analytics/utils';
export function fetchBoost(r) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        const { apiUrl, shop } = AppConfig.searchSettings.boost;
        let collectionHandle = undefined;
        let currentPage = 'collection_page';
        const searchApp = document.querySelector('#search-app');
        if (searchApp) {
            collectionHandle = searchApp.dataset.collectionHandle || '0';
            currentPage = searchApp.dataset.page || 'collection_page';
        }
        const params = new URLSearchParams({
            page: String(r.page || 1),
            limit: String(r.pageSize || 24),
            shop,
            build_filter_tree: 'true',
            return_all_currency_fields: 'false',
            // NOTE: insert_banners is a custom parameter Boost implemented specifically for Aquila.
            // It is not part of the standard Boost Commerce API and may not be available on other tenants.
            insert_banners: 'true',
            pg: currentPage,
            // Analytics capture params. Per Boost's dev doc, search/filter/suggest
            // analytics are pulled SERVER-SIDE from these API requests (not from /e
            // events) — so the request must identify the session the same way the
            // official bundle does, or our searches/filters are invisible to Boost's
            // Search & Collections analytics.
            sid: getBoostSessionId(),
            event_type: 'init',
        });
        // `q` only for keyword searches. On the storefront, Boost's app-embed script
        // rewrites any /filter request carrying `q` (even `q=*`) to its Search API,
        // which ranks by search logic and skips collection merchandising — so a
        // collection page without a search term must not send it. (AQ-1095)
        if (r.q || currentPage !== 'collection_page')
            params.set('q', r.q || '*');
        // Joins the request to the logged-in shopper; omitted for guests.
        const customerId = getCustomerId();
        if (customerId)
            params.append('customer_id', customerId);
        if (r.sort) {
            const sortMap = {
                'title:asc': 'title-ascending',
                'title:desc': 'title-descending',
                'price:asc': 'price-ascending',
                'price:desc': 'price-descending',
                'created:asc': 'created-ascending',
                'created:desc': 'created-descending',
                'best-selling': 'best-selling',
                'manual': 'manual',
                'relevance': 'relevance',
                // Legacy mapping
                title_asc: 'title-ascending',
                title_desc: 'title-descending',
                price_asc: 'price-ascending',
                price_desc: 'price-descending',
            };
            params.append('sort', sortMap[r.sort] || r.sort);
        }
        // Handle filters
        if (r.filters) {
            Object.entries(r.filters).forEach(([filterId, values]) => {
                if (values.length) {
                    // Handle special filters
                    if (filterId === 'available') {
                        params.append('product_available', values[0]);
                    }
                    else if (filterId === 'variant_available') {
                        params.append('variant_available', values[0]);
                    }
                    else if (filterId === 'tag') {
                        values.forEach(value => {
                            params.append('tag[]', value);
                        });
                    }
                    else if (filterId.startsWith('metafield.')) {
                        // Handle metafield filters (pf_* format)
                        const metafieldKey = filterId.replace('metafield.', 'pf_');
                        values.forEach(value => {
                            params.append(`${metafieldKey}[]`, value);
                        });
                    }
                    else if (filterId === 'price') {
                        // Handle price range filter
                        const [min, max] = values[0].split(',');
                        params.append('pf_p_price_min', min);
                        params.append('pf_p_price_max', max);
                    }
                    else {
                        // Standard filters
                        values.forEach(value => {
                            params.append(`${filterId}[]`, value);
                        });
                    }
                }
            });
        }
        // Handle collection search
        if (collectionHandle && collectionHandle !== '0') {
            params.append('collection_scope', collectionHandle);
        }
        // Add AI features
        if (r.fuzzy) {
            params.append('fuzzy', 'true');
        }
        if (r.reduce_min_match) {
            params.append('reduce_min_match', 'true');
        }
        // Add merchandising parameters
        if (r.merchandising) {
            if ((_a = r.merchandising.boostProducts) === null || _a === void 0 ? void 0 : _a.length) {
                params.append('boost_products', r.merchandising.boostProducts.join(','));
            }
            if ((_b = r.merchandising.hideProducts) === null || _b === void 0 ? void 0 : _b.length) {
                params.append('hide_products', r.merchandising.hideProducts.join(','));
            }
            if ((_c = r.merchandising.pinProducts) === null || _c === void 0 ? void 0 : _c.length) {
                r.merchandising.pinProducts.forEach((pin, index) => {
                    params.append(`pin_products[${index}][id]`, pin.productId);
                    params.append(`pin_products[${index}][position]`, String(pin.position));
                });
            }
        }
        const url = `${apiUrl}${currentPage === 'collection_page' ? '/filter' : '/search'}?${params.toString()}`;
        const response = yield fetch(url, {
            headers: {
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Boost API error: ${response.statusText}`);
        }
        return response.json();
    });
}
export function fetchBoostSearchSuggest(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, page = 1, limit = 24) {
        const { apiUrl, shop } = AppConfig.searchSettings.boost;
        const params = new URLSearchParams({
            q: query,
            shop,
            limit: String(limit),
            page: String(page),
            event_type: 'suggest',
            suggest_types: 'products,pages,collections,suggestions,did_you_mean',
            suggest_fields: 'title,product_type,vendor,tags',
            dym_limit: '3',
            // Session id for Boost's server-side suggest analytics (see fetchBoost).
            sid: getBoostSessionId(),
        });
        const customerId = getCustomerId();
        if (customerId)
            params.append('customer_id', customerId);
        const url = `${apiUrl}/search/suggest?${params.toString()}`;
        const response = yield fetch(url, {
            headers: {
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Boost Search Suggest API error: ${response.statusText}`);
        }
        return response.json();
    });
}
export function fetchBoostSuggestions(query_1) {
    return __awaiter(this, arguments, void 0, function* (query, limit = 10) {
        const { apiUrl, shop } = AppConfig.searchSettings.boost;
        const params = new URLSearchParams({
            q: query,
            shop,
            limit: String(limit),
            product_limit: String(Math.floor(limit * 0.7)), // 70% products
            page_limit: String(Math.floor(limit * 0.3)) // 30% pages
        });
        const url = `${apiUrl}/search/suggest?${params.toString()}`;
        const response = yield fetch(url, {
            headers: {
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Boost Suggest API error: ${response.statusText}`);
        }
        return response.json();
    });
}
/**
 * Extract Boost recs payload options from a mount element's `data-*` attributes.
 * Only returns fields that are actually present so callers can spread over
 * explicit overrides.
 *
 * Fields deliberately excluded from dataset reading — they only belong on the
 * API body / in programmatic `recsOptions`, not in authored HTML:
 *   - `secondaryAlgorithm` — developer-level AI-fallback choice.
 *   - `ruleBased` — complex array shape; can't round-trip through a data-* string.
 */
export function readBoostRecsOptionsFromDataset(el) {
    const ds = el === null || el === void 0 ? void 0 : el.dataset;
    if (!ds)
        return {};
    const out = {};
    if (ds.widgetId)
        out.widgetId = ds.widgetId;
    if (ds.pageType)
        out.pageType = ds.pageType;
    if (ds.limit)
        out.limit = Number(ds.limit);
    if (ds.locale)
        out.locale = ds.locale;
    if (ds.layoutDisplay)
        out.layoutDisplay = ds.layoutDisplay;
    if (ds.modelType)
        out.modelType = ds.modelType;
    if (ds.customerId)
        out.customerId = ds.customerId;
    if (ds.calculatedBasedOn === 'view-events' || ds.calculatedBasedOn === 'purchase-events') {
        out.calculatedBasedOn = ds.calculatedBasedOn;
    }
    if (ds.rangeOfTime)
        out.rangeOfTime = ds.rangeOfTime;
    if (ds.configSource === 'boost-app' || ds.configSource === 'manual')
        out.configSource = ds.configSource;
    return out;
}
/**
 * Wait for the Boost widget-integration bundle to finish initialising.
 *
 * Mirrors the pattern Boost's own `bc-widget-integration.js` uses to gate its
 * init: poll `window.boostWidgetIntegration.status === "ready"` on a 100ms
 * interval. Boost itself retries up to ~10s; we use a tighter ceiling here so a
 * stalled bundle can't hang the recs carousel.
 *
 * Resolves `true` once status flips to "ready", or `false` on timeout. The
 * "ready" state is the gate Boost flips after `recommendationWidgets` is
 * populated, so it's the right signal for reading `params` from the global.
 */
export function waitForBoostWidgetIntegrationReady(timeoutMs = 2000, intervalMs = 100) {
    const isReady = () => { var _a; return ((_a = window.boostWidgetIntegration) === null || _a === void 0 ? void 0 : _a.status) === 'ready'; };
    if (isReady())
        return Promise.resolve(true);
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
            if (isReady())
                return resolve(true);
            if (Date.now() - start >= timeoutMs)
                return resolve(false);
            setTimeout(tick, intervalMs);
        };
        setTimeout(tick, intervalMs);
    });
}
/**
 * Fetch Boost product recommendations.
 * See: https://support.boostcommerce.net/en/articles/11982104-for-dev-recommendation-api
 *
 * POSTs to `/discovery/recommend` on the Boost services origin (the `BOOST_API_URL`
 * env var points at the `/bc-sf-filter` path, so we resolve the recs path relative
 * to its origin).
 *
 * Field placement mirrors Boost's own widget-integration bundle:
 *   - QUERY ONLY:  event_type ("init"), pg (page type, always sent)
 *   - BOTH:        shop, wid, recommendationType, limit, layoutDisplay, locale
 *   - BODY ONLY:   productIds, collectionId, customerId, modelType, secondaryAlgorithm,
 *                  ruleBased, calculatedBasedOn, rangeOfTime, return_all_currency_fields
 */
export function fetchBoostRecs(r, signal) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b;
        const { apiUrl, shop } = AppConfig.searchSettings.boost;
        const recommendationType = r.recsType || 'related-items';
        // Boost widget IDs are provisioned in the Boost admin. For PDP we can synthesise
        // a sensible default from the seed; for non-seed types (bestsellers, etc.) the
        // caller must pass a real widgetId or Boost will respond with an empty set.
        const widgetId = r.widgetId
            || (r.seedProductId ? `productpage-${r.seedProductId}` : '');
        const locale = r.locale
            || (typeof document !== 'undefined' ? document.documentElement.lang : '')
            || 'en';
        // Params config: the six fields Boost duplicates in both places, plus
        // event_type (hardcoded) and pg (only when the caller supplies it).
        const paramConfig = {
            event_type: 'init',
            shop,
            recommendationType,
            limit: String((_a = r.limit) !== null && _a !== void 0 ? _a : 12),
            layoutDisplay: r.layoutDisplay || 'carousel',
            locale,
        };
        if (widgetId)
            paramConfig.wid = widgetId;
        if (r.pageType)
            paramConfig.pg = r.pageType;
        // Body-payload config: the same six duplicated fields, plus return_all_currency_fields
        // and whichever body-only fields the caller supplied.
        const bodyPayloadConfig = {
            shop,
            recommendationType,
            limit: (_b = r.limit) !== null && _b !== void 0 ? _b : 12,
            layoutDisplay: r.layoutDisplay || 'carousel',
            wid: widgetId,
            locale,
            return_all_currency_fields: false,
        };
        if (r.seedProductId)
            bodyPayloadConfig.productIds = [Number(r.seedProductId)];
        // `related-items` w/ a seed defaults to the "Alternative" AI model when the caller
        // didn't specify one — matches the Boost admin default for PDP widgets.
        if (r.modelType) {
            bodyPayloadConfig.modelType = r.modelType;
        }
        else if (recommendationType === 'related-items' && r.seedProductId) {
            bodyPayloadConfig.modelType = 'Alternative';
        }
        if (r.secondaryAlgorithm)
            bodyPayloadConfig.secondaryAlgorithm = r.secondaryAlgorithm;
        if (r.ruleBased && r.ruleBased.length > 0)
            bodyPayloadConfig.ruleBased = r.ruleBased;
        if (r.rules && r.rules.length > 0)
            bodyPayloadConfig.rules = r.rules;
        if (r.customerId)
            bodyPayloadConfig.customerId = r.customerId;
        if (r.calculatedBasedOn)
            bodyPayloadConfig.calculatedBasedOn = r.calculatedBasedOn;
        if (r.rangeOfTime)
            bodyPayloadConfig.rangeOfTime = r.rangeOfTime;
        const response = yield fetch(`${new URL('/discovery/recommend', apiUrl)}?${new URLSearchParams(paramConfig)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyPayloadConfig),
            signal,
        });
        if (!response.ok) {
            throw new Error(`Boost Recommendations API error: ${response.statusText}`);
        }
        return response.json();
    });
}
export function fetchBoostContent(query_1, contentType_1) {
    return __awaiter(this, arguments, void 0, function* (query, contentType, page = 1, limit = 24) {
        const { apiUrl, shop } = AppConfig.searchSettings.boost;
        const params = new URLSearchParams({
            q: query || '*',
            shop,
            page: String(page),
            limit: String(limit),
            build_filter_tree: 'true',
        });
        // Use the search endpoint which returns products, pages, and collections
        const url = `${apiUrl}/search?${params.toString()}`;
        const response = yield fetch(url, {
            headers: {
                'Accept-Encoding': 'gzip',
            },
        });
        if (!response.ok) {
            throw new Error(`Boost Content API error: ${response.statusText}`);
        }
        return response.json();
    });
}
